import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, MailOpen, MousePointerClick, Send, AlertOctagon } from "lucide-react";

const RANGES = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

const TILES = [
  {
    key: "sent",
    label: "Sent",
    icon: Send,
    tone: "text-slate-700",
    bg: "bg-slate-50 border-slate-200",
  },
  {
    key: "delivered",
    label: "Delivered",
    icon: Mail,
    tone: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
  },
  {
    key: "opened",
    label: "Opened",
    icon: MailOpen,
    tone: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    rateKey: "open_rate_pct",
  },
  {
    key: "clicked",
    label: "Clicked",
    icon: MousePointerClick,
    tone: "text-violet-700",
    bg: "bg-violet-50 border-violet-200",
    rateKey: "click_rate_pct",
  },
  {
    key: "bounced",
    label: "Bounced",
    icon: AlertOctagon,
    tone: "text-rose-700",
    bg: "bg-rose-50 border-rose-200",
  },
];

export default function EmailEngagementWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/dashboard/email-engagement?days=${days}`)
      .then(({ data }) => !cancelled && setData(data))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  // Hide entirely when there's nothing meaningful to show.
  if (!loading && data && !data.sent) return null;

  return (
    <div
      data-testid="email-engagement-widget"
      className="mt-6 border border-slate-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <Mail className="h-3 w-3" />
          Candidate email engagement
        </div>
        <div className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white p-1 text-[11px]">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              data-testid={`engagement-range-${r.value}`}
              onClick={() => setDays(r.value)}
              className={`rounded-full px-2.5 py-0.5 transition ${
                days === r.value
                  ? "bg-blue-600 font-semibold text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-5">
        {TILES.map((t) => {
          const Icon = t.icon;
          const v = (data && data[t.key]) || 0;
          const rate = t.rateKey && data ? data[t.rateKey] : null;
          return (
            <div
              key={t.key}
              data-testid={`engagement-tile-${t.key}`}
              className={`border ${t.bg} p-3`}
            >
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">
                <span>{t.label}</span>
                <Icon className={`h-3.5 w-3.5 ${t.tone}`} />
              </div>
              <div className={`mt-1 text-2xl font-bold ${t.tone}`}>{v}</div>
              {rate !== null && rate !== undefined && data?.sent > 0 ? (
                <div className="text-[11px] text-slate-500">{rate}% rate</div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="border-t border-slate-100 bg-slate-50 px-5 py-2 text-[11px] text-slate-500">
        Powered by Resend delivery webhooks. Engagement timestamps stamp on each
        candidate row — sort by Opened / Clicked from the Applicants page to
        triage your warmest leads.
      </div>
    </div>
  );
}
